import { useState, useEffect } from "react";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import UnderwritingRuleResultsApiClient, { UnderwritingRuleResultReadDTO } from "@/api/underwritingRuleResultsApiClient";
import { sortBy } from "lodash";

export interface UseUnderwritingRuleResultsParams {
  parentEntityType: string;
  parentEntityId: string;
}

export interface UseUnderwritingRuleResultsReturn {
  ruleResults: UnderwritingRuleResultReadDTO[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useUnderwritingRuleResults = ({
  parentEntityType,
  parentEntityId,
}: UseUnderwritingRuleResultsParams): UseUnderwritingRuleResultsReturn => {
  const [ruleResults, setRuleResults] = useState<UnderwritingRuleResultReadDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const apiClient = useAuthenticatedApiClient(UnderwritingRuleResultsApiClient);

  const fetchRuleResults = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, get the rule result IDs
      const idsResponse = await apiClient.getRuleResultIdsByParentEntity(parentEntityType, parentEntityId);

      if (!idsResponse || !Array.isArray(idsResponse)) {
        setRuleResults([]);
        return;
      }

      // Then fetch each rule result by ID
      const ruleResultsPromises = idsResponse.map(async (id: string) => {
        const resultResponse = await apiClient.getRuleResultById(id);
        return resultResponse;
      });

      const results = await Promise.all(ruleResultsPromises);
      
      // Sort results by friendly ID in ascending order
      const sortedResults = sortBy(results, 'ruleFriendlyId');
      setRuleResults(sortedResults);
    } catch (err) {
      console.error("Error fetching underwriting rule results:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch underwriting rule results");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (parentEntityType && parentEntityId) {
      fetchRuleResults();
    }
  }, [parentEntityType, parentEntityId]);

  return {
    ruleResults,
    loading,
    error,
    refetch: fetchRuleResults,
  };
};
